/**
 * This file provides utility functions to collect filter metrics
 */

import {
    MetricsService,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import _ from 'lodash';
import {
    AnswerColumnEffectiveType,
    ColumnTimeBucket,
    FalconDataType,
    MixpanelAnswerCommonPropertiesDocument,
} from '/@services/generated/graphql-types';
import { DataPanelView } from '../data-panel-v2/data-panel-v2.util';
import { FALCON_DATE_DATA_TYPES } from './answer-filter.util';
import { NUMERICAL_DATE_BUCKETS } from './date-range-filter/date-range-filter.util';

export enum COLUMN_DATA_TYPE {
    NUMBER = 'Number',
    STRING = 'String',
    BOOLEAN = 'Boolean',
    DATE = 'Date',
}

export enum INITIATE_FILTER_ADDITION_SOURCES {
    COLUMN_HEADER = 'Column_header',
    AXIS_MENU = 'Axis_menu',
    LEFT_NAV_PANEL_FILTER_ICON = 'Left_nav_panel_filter_icon',
    PINBOARD_LEFT_NAV_PANEL_FILTER_ICON = 'Pinboard_left_nav_panel_filter_icon',
    RIGHT_CLICK_TO_INCLUDE_EXCLUDE_FILTER = 'Right_click_to_include_exclude_filter',
}

export enum ADD_FILTER_SOURCES {
    FILTER_MODAL = 'Filter_modal',
    SAGE = 'Sage',
    RIGHT_CLICK_ONLY_INCLUDE_FILTER = 'Right_click_only_include_filter',
    RIGHT_CLICK_EXCLUDE_FILTER = 'Right_click_exclude_filter',
}

export enum REMOVE_FILTER_SOURCES {
    FILTER_CHIP = 'Filter_Chip',
    SAGE = 'Sage',
    LEFT_PANEL = 'Left_panel',
}

export type CollectEventFunction = (
    userAction: UserAction,
    properties?: any,
) => void;

export type AddPinboardFilterMetricType = {
    totalVizesFilterCanApplyTo?: number;
    totalVizesFiltersIsAppliedTo?: number;
    selectiveFiltering?: string;
    linkedFilters?: string;
    totalSecondaryFiltersInLinkedFilters?: number;
    mandatoryFilter?: string;
    totalTabsFilterCanApplyTo?: number;
    totalTabsFilterIsAppliedTo?: number;
    isSingleValueSelect?: string;
    isFilterRenamed?: string;
    type?: string;
};

export function getMetricsColumnDataType(
    falconDataType: FalconDataType,
    columnType: AnswerColumnEffectiveType,
    timeBucket: ColumnTimeBucket,
): COLUMN_DATA_TYPE {
    if (columnType === AnswerColumnEffectiveType.Measure) {
        return COLUMN_DATA_TYPE.NUMBER;
    }
    if (columnType === AnswerColumnEffectiveType.Attribute) {
        if (
            FALCON_DATE_DATA_TYPES.includes(falconDataType) ||
            NUMERICAL_DATE_BUCKETS.includes(timeBucket)
        ) {
            return COLUMN_DATA_TYPE.DATE;
        }

        if (falconDataType === FalconDataType.Bool) {
            return COLUMN_DATA_TYPE.BOOLEAN;
        }

        return COLUMN_DATA_TYPE.STRING;
    }
    return '' as COLUMN_DATA_TYPE;
}

export function collectEventWithMixpanelData(
    metricsService: MetricsService,
    getAnswerMixpanelProperties: () => any,
) {
    const answerCommonProperties = getAnswerMixpanelProperties();

    return (userAction: UserAction, properties: any = {}) => {
        metricsService.collectEvent(userAction, {
            ...answerCommonProperties,
            ...properties,
        });
    };
}

export function collectInitiateFilterAdditionEvent(
    collectEvent: CollectEventFunction,
    source: INITIATE_FILTER_ADDITION_SOURCES,
    dataType: COLUMN_DATA_TYPE,
    columnSearchQueryLength = 0,
    viewName?: DataPanelView,
) {
    collectEvent(
        new UserAction(userActionTypeEnum.CLICK_TO_INITIATE_FILTER_ADDITION),
        {
            source,
            dataType,
            columnSearchQueryLength,
            viewName: viewName || '',
        },
    );
}

export function collectShowPossibleValuesEvent(
    collectEvent: CollectEventFunction,
    dataType: COLUMN_DATA_TYPE,
    enabled: boolean,
) {
    collectEvent(new UserAction(userActionTypeEnum.SHOW_ALL_POSSIBLE_VALUES), {
        dataType,
        enabled,
    });
}

export function collectAddValuesInBulkEvent(
    collectEvent: CollectEventFunction,
    dataType: COLUMN_DATA_TYPE,
) {
    collectEvent(new UserAction(userActionTypeEnum.ADD_VALUES_IN_BULK), {
        dataType,
    });
}

export function collectAddedValuesInBulkEvent(
    collectEvent: CollectEventFunction,
    dataType: COLUMN_DATA_TYPE,
) {
    collectEvent(new UserAction(userActionTypeEnum.ADDED_VALUES_IN_BULK), {
        dataType,
    });
}

export function collectAddFilterEvent(
    collectEvent: CollectEventFunction,
    dataType: COLUMN_DATA_TYPE,
    source: ADD_FILTER_SOURCES,
    numberOfIncludedValues?: number,
    numberOfExcludedValues?: number,
    defaultValues?: string,
    numberOfConditions?: number,
    pinboardFilterMetrics?: AddPinboardFilterMetricType,
) {
    collectEvent(new UserAction(userActionTypeEnum.ADD_FILTER_TO_ANSWER), {
        dataType,
        source,
        ...(!_.isNil(numberOfIncludedValues) && { numberOfIncludedValues }),
        ...(!_.isNil(numberOfExcludedValues) && { numberOfExcludedValues }),
        ...(!_.isNil(defaultValues) && { defaultValues }),
        ...(!_.isNil(numberOfConditions) && { numberOfConditions }),
        ...(!_.isNil(pinboardFilterMetrics) && { ...pinboardFilterMetrics }),
    });
}

export function collectRemoveFilterEvent(
    collectEvent: CollectEventFunction,
    dataType: COLUMN_DATA_TYPE,
    source: REMOVE_FILTER_SOURCES,
) {
    collectEvent(new UserAction(userActionTypeEnum.REMOVE_FILTER), {
        dataType,
        source,
    });
}
